import { useMemo } from "react";
import { useQuery } from "convex/react";
import {
  extractScoresFromResult,
  SyntheseCampagneTable,
  TEINTES_PASTILLE,
  type ColonneSynthese,
  type EvaluationData,
  type LigneSynthese,
  type TraitResult,
} from "@repo/results";
import { calculerScores } from "@repo/utils";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import type {
  StatDimension,
  StatFourchette,
} from "../../../convex/referentiel/agregation";
import {
  niveauxForcesQsort,
  seuilReferentsFiable,
} from "../../../convex/referentiel/agregation";
import type { ResultatComparaison } from "../../../convex/referentiel/comparer";
import {
  comparerFourchette,
  comparerQsort,
} from "../../../convex/referentiel/comparer";
import { scoreDuNiveau } from "../../../convex/referentiel/qsort";
import { interpreterCorrelation } from "../../../convex/referentiel/rapport";
import type {
  EvaluationFitCulturelRow,
  EvaluationPamiRow,
} from "./referentiel/ReferentielMatchingTab";

type Props = {
  campagneId: Id<"campagnes">;
  /** Tous les candidats de la campagne. Ne deviennent des lignes que ceux qui
   *  ont au moins un score : une ligne de tirets ne se compare à rien. */
  parcours: {
    _id: Id<"parcoursEvaluations">;
    prenomCandidat?: string;
    nomCandidat?: string;
  }[];
  /** Passations ent-struct (`undefined` = chargement). */
  evaluations: EvaluationData[] | undefined;
  evaluationsPami: EvaluationPamiRow[] | undefined;
  evaluationsFitCulturel: EvaluationFitCulturelRow[] | undefined;
  /** Tests que la campagne fait passer — décide des colonnes présentes. */
  aPami: boolean;
  aFitCulturel: boolean;
  aEntStruct: boolean;
};

/**
 * Comparaison au référentiel d'une cible, par `parcoursId`.
 *
 * Même moteur que `ReferentielMatchingTab` — `comparerFourchette` / `comparerQsort`
 * contre la DERNIÈRE version du référentiel, la seule contre laquelle tous les
 * candidats sont comparables entre eux. On ne garde ici que le score global : le
 * détail par dimension est le sujet de l'onglet Matching.
 *
 * Le hook est appelé une fois par cible ; il rend une map vide tant que le
 * référentiel n'existe pas ou n'a aucune version.
 */
function useScoresMatching(
  campagneId: Id<"campagnes">,
  cible: "pami" | "pc-fit",
  actif: boolean,
  evaluationsPami: EvaluationPamiRow[] | undefined,
  evaluationsFitCulturel: EvaluationFitCulturelRow[] | undefined,
): Map<string, ResultatComparaison | null> {
  const etat = useQuery(
    api.referentiel.campagne.obtenirEtatReferentiel,
    actif ? { campagneId, cible } : "skip",
  );

  const versions = etat?.historique ?? [];
  const version = versions[versions.length - 1];

  const echelleConfig = etat?.config.echelle;
  const echelle = useMemo(
    () =>
      echelleConfig?.kind === "fourchette"
        ? { min: echelleConfig.min, max: echelleConfig.max }
        : null,
    [echelleConfig],
  );
  const pyramide = useMemo(
    () => (echelleConfig?.kind === "qsort" ? echelleConfig.pyramide : null),
    [echelleConfig],
  );

  const niveauxForces = useMemo(() => {
    if (!etat || !version || !pyramide) return undefined;
    return (
      version.niveauxForces ??
      niveauxForcesQsort({
        dimensions: etat.config.dimensions,
        agregat: version.dimensions as Record<string, StatDimension>,
        pyramide,
      })
    );
  }, [etat, version, pyramide]);

  return useMemo(() => {
    const scores = new Map<string, ResultatComparaison | null>();
    if (!etat || !version) return scores;

    if (cible === "pami" && echelle && evaluationsPami) {
      const agregat = version.dimensions as Record<string, StatFourchette>;
      for (const row of evaluationsPami) {
        const scoresCandidat = row.scoresJson
          ? extractScoresFromResult(JSON.parse(row.scoresJson) as TraitResult[])
          : null;
        scores.set(
          row.parcoursId,
          scoresCandidat
            ? comparerFourchette({
                dimensions: etat.config.dimensions,
                scoresCandidat,
                agregat,
                echelle,
                nbReferents: version.nbReferents,
                codesDesaccord: version.dimensionsEnDesaccord,
                // PAMI : référent unique, le seuil de 3 marquerait toujours
                // `faible`.
                seuilReferents: seuilReferentsFiable(etat.config.cible),
              })
            : null,
        );
      }
    }

    if (cible === "pc-fit" && pyramide && evaluationsFitCulturel) {
      const agregat = version.dimensions as Record<string, StatDimension>;
      for (const row of evaluationsFitCulturel) {
        // Score RECALCULÉ depuis le niveau et la pyramide de la campagne : le
        // score stocké dans la réponse n'est qu'un confort de relecture.
        const vecteurCandidat = Object.fromEntries(
          row.niveaux.map(({ code, niveau }) => [
            code,
            scoreDuNiveau(niveau, pyramide.length),
          ]),
        );
        scores.set(
          row.parcoursId,
          comparerQsort({
            dimensions: etat.config.dimensions,
            vecteurCandidat,
            agregat,
            pyramide,
            nbReferents: version.nbReferents,
            niveauxForces,
          }),
        );
      }
    }

    return scores;
  }, [
    cible,
    etat,
    version,
    echelle,
    pyramide,
    niveauxForces,
    evaluationsPami,
    evaluationsFitCulturel,
  ]);
}

/**
 * Couverture globale du barème ent-struct par `parcoursId`.
 *
 * Ce n'est PAS un matching à un référentiel : l'entretien structuré n'a pas de
 * référents, son étalon est le barème de son template. La colonne se nomme donc
 * « Couverture » et non « Matching ».
 *
 * Les candidats d'une campagne sont tous sur le MÊME barème, donc directement
 * comparables : `evaluationsConfig` est fixé à la création de la campagne, le
 * rattachement d'un parcours est refusé si sa config diffère
 * (`configsCompatibles`), et les templates sont des fichiers versionnés — un
 * même nom de template porte toujours les mêmes poids.
 */
function useCouvertureEntStruct(evaluations: EvaluationData[] | undefined) {
  return useMemo(() => {
    const couverture = new Map<string, number | null>();
    if (!evaluations) return couverture;

    for (const evaluation of evaluations) {
      const scores = evaluation.questionsEtat
        ? calculerScores(evaluation.questionsEtat)
        : null;
      // `scoreGlobalObtenu / scoreGlobalMax` = Σ(taux × poids) / Σ poids : la
      // pondération du barème est déjà dedans.
      couverture.set(
        evaluation.parcoursId,
        scores && scores.scoreGlobalMax > 0
          ? (scores.scoreGlobalObtenu / scores.scoreGlobalMax) * 100
          : null,
      );
    }
    return couverture;
  }, [evaluations]);
}

/**
 * Onglet Synthèse : le récapitulatif transverse de la campagne — un candidat par
 * ligne, un score global par test.
 *
 * Seul onglet qui ignore le sélecteur de test de la page : c'est précisément son
 * objet de mettre les tests côte à côte.
 */
export function SyntheseTab({
  campagneId,
  parcours,
  evaluations,
  evaluationsPami,
  evaluationsFitCulturel,
  aPami,
  aFitCulturel,
  aEntStruct,
}: Props) {
  const scoresPami = useScoresMatching(
    campagneId,
    "pami",
    aPami,
    evaluationsPami,
    undefined,
  );
  const scoresFitCulturel = useScoresMatching(
    campagneId,
    "pc-fit",
    aFitCulturel,
    undefined,
    evaluationsFitCulturel,
  );
  const couverture = useCouvertureEntStruct(
    aEntStruct ? evaluations : undefined,
  );

  // Une colonne n'existe que si la campagne fait passer le test : une colonne
  // entièrement vide ne dit rien de plus qu'une colonne absente.
  const colonnes = useMemo<ColonneSynthese[]>(
    () =>
      [
        aPami && {
          key: "pami",
          label: "Personnalité et Motivations",
          aide:
            "Score de matching du profil PAMI du candidat aux fourchettes attendues par les référents, sur 100.",
        },
        aFitCulturel && {
          key: "fitculturel",
          label: "PC Fit",
          aide:
            "Verdict du PC Fit, tiré de la moyenne des corrélations entre le classement du candidat et celui de chaque référent (de −1, priorités inversées, à +1, accord ; le hasard vaut 0). Le tri suit cette moyenne.",
          // Le MOT du rapport, pas r : c'est lui qui se lit (arbitrage du
          // 30/10/2026). Teinte par verdict — « Éloigné » ne se confond plus
          // avec « Pas de tendance nette », qui avaient la même pastille grise.
          pastille: (ligne: LigneSynthese) => {
            const { sens, aLaLimite, libelle } = interpreterCorrelation(
              scoresFitCulturel.get(ligne.parcoursId)?.correlation ?? null,
            );
            return {
              texte: libelle,
              classes:
                sens === "proche"
                  ? aLaLimite
                    ? TEINTES_PASTILLE.moyenne
                    : TEINTES_PASTILLE.haute
                  : sens === "eloigne"
                    ? TEINTES_PASTILLE.alerte
                    : TEINTES_PASTILLE.basse,
            };
          },
        },
        aEntStruct && {
          key: "entstruct",
          label: "Compétences",
          aide:
            "Couverture du barème de l'entretien FairView : moyenne des taux par compétence, pondérée par leur poids dans le template. Ce n'est pas un matching à un référentiel de référents — l'entretien porte déjà sa cible.",
        },
      ].filter(Boolean) as ColonneSynthese[],
    [aPami, aFitCulturel, aEntStruct, scoresFitCulturel],
  );

  // Un candidat scoré sur un test seulement reste : sa ligne compare déjà
  // quelque chose, le tiret dit ce qui manque.
  const lignes = useMemo<LigneSynthese[]>(
    () =>
      parcours
        .map((p): LigneSynthese => ({
          parcoursId: p._id,
          prenomCandidat: p.prenomCandidat,
          nomCandidat: p.nomCandidat,
          scores: {
            pami: scoresPami.get(p._id)?.scoreMatching ?? null,
            fitculturel: scoresFitCulturel.get(p._id)?.scoreMatching ?? null,
            entstruct: couverture.get(p._id) ?? null,
          },
        }))
        .filter((ligne) =>
          colonnes.some((c) => (ligne.scores[c.key] ?? null) !== null),
        ),
    [parcours, scoresPami, scoresFitCulturel, couverture, colonnes],
  );

  return (
    <div className="space-y-4">
      <SyntheseCampagneTable lignes={lignes} colonnes={colonnes} />

      {lignes.length > 0 && (
        <p className="text-muted-foreground text-xs">
          Un tiret signale l'absence de mesure : test non passé, ou référentiel
          pas encore figé. Aucune colonne n'agrège les autres — les échelles ne
          sont pas commensurables, la comparaison se fait colonne par colonne.
        </p>
      )}
    </div>
  );
}
